// InfoSharing.tsx - API 연동된 정보공유 게시판 컴포넌트

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CommunityLayout from '../../../components/community/CommunityLayout';
import NoticeCard from '../../../components/common/NoticeCard';
import { getPostList, PostListItem, ApiResponse, PostListResponse } from '../../../services/postApi';
import { searchInfoSharingPosts } from '../../../services/searchApi';
import type { PostSearchItem } from '../../../services/searchApi';

// HTML 태그 제거 함수
const stripHtml = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const InfoSharing: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [postList, setPostList] = useState<PostListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  
  // 검색 관련 상태
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // 게시글 목록 조회
  const fetchPosts = useCallback(async (pageNum: number = 0, reset: boolean = false) => {
    console.log('InfoSharing fetchPosts 호출됨:', { pageNum, reset, loading });
    if (loading) return;
    
    setLoading(true);
    try {
      console.log('API 요청 시작 - board: information');
      const response: ApiResponse<PostListResponse> = await getPostList(
        'information',
        pageNum,
        10
      );

      console.log('InfoSharing API 응답:', response);
      if (response.data) {
        const newPosts = response.data.content;
        console.log('받은 게시글 수:', newPosts.length);
        setPostList(reset ? newPosts : prev => [...prev, ...newPosts]);
        setHasMorePosts(!response.data.last);
      }
    } catch (error: any) {
      console.error('게시글 목록 조회 실패:', error);
      if (error.message === 'Unauthorized') {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    if (!isSearchMode) {
      fetchPosts(0, true);
    }
  }, [fetchPosts, isSearchMode]);

  // URL에서 검색 키워드 확인 및 검색 실행
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    if (keyword && keyword.trim()) {
      setSearchValue(keyword);
      setIsSearchMode(true);
      performSearch(keyword);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [searchParams]);

  // 검색 실행 함수
  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = await searchInfoSharingPosts(keyword);
      setSearchResults(results);
    } catch (error) {
      console.error('정보공유 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 페이지 로드 더보기
  const loadMorePosts = () => {
    if (hasMorePosts && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage, false);
    }
  };

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    navigate(`/community/post/${postId}`);
  };
  
  // 공지사항 필터링 (pinned=true인 게시글)
  const pinnedPosts = postList.filter(post => post.pinned);
  const regularPosts = postList.filter(post => !post.pinned);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // URL 파라미터 변경하여 검색 실행
        navigate(`/community/info-sharing?keyword=${encodeURIComponent(searchTerm)}`);
      } else {
        // 검색어가 비어있으면 기본 페이지로 이동
        navigate('/community/info-sharing');
      }
    }
  };

  return (
    <CommunityLayout hideFooter={true}>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>정보 공유</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>정보공유</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="검색어를 입력해주세요."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 상단고정 공지사항 */}
          {pinnedPosts.map(notice => (
            <NoticeCard 
              key={notice.post_id}
              title={notice.title}
              onClick={() => handlePostClick(notice.post_id)}
            />
          ))}

          {/* 게시글 목록 */}
          <InfoList>
            {isSearchMode ? (
              // 검색 모드
              <>
                {searchLoading ? (
                  <LoadingState>검색 중...</LoadingState>
                ) : searchError ? (
                  <EmptyState>{searchError}</EmptyState>
                ) : searchResults.length === 0 ? (
                  <EmptyState>'{searchValue}'에 대한 검색 결과가 없습니다.</EmptyState>
                ) : (
                  searchResults.map((post, index) => (
                    <React.Fragment key={post.id}>
                      <InfoCard onClick={() => handlePostClick(post.id)}>
                        <InfoContent>
                          <InfoHeader>
                            <InfoTitle>{post.title}</InfoTitle>
                            <InfoDescription>
                              {stripHtml(post.htmlcontent).length > 100 
                                ? stripHtml(post.htmlcontent).substring(0, 100) + '...' 
                                : stripHtml(post.htmlcontent)}
                            </InfoDescription>
                          </InfoHeader>
                          
                          <InfoFooter>
                            <InfoAuthorInfo>
                              <AuthorAvatar>
                                <img src={post.writerProfileImageUrl || "/images/default-avatar.png"} alt="작성자" />
                              </AuthorAvatar>
                              <AuthorName>{post.writerName}</AuthorName>
                              <Divider />
                              <InfoDate>{new Date(post.postDate).toLocaleDateString('ko-KR')}</InfoDate>
                            </InfoAuthorInfo>
                            
                            <InfoStats>
                              <StatItem>댓글 -</StatItem>
                              <Divider />
                              <StatItem>조회수 -</StatItem>
                            </InfoStats>
                          </InfoFooter>
                        </InfoContent>
                      </InfoCard>
                      
                      {index < searchResults.length - 1 && <InfoDivider />}
                    </React.Fragment>
                  ))
                )}
              </>
            ) : (
              // 기본 모드
              <>
                {loading ? (
                  <LoadingState>
                    게시글을 불러오는 중...
                  </LoadingState>
                ) : regularPosts.length === 0 ? (
                  <EmptyState>
                    작성된 게시글이 없습니다.
                  </EmptyState>
                ) : (
                  regularPosts.map((post: PostListItem, index: number) => (
                    <React.Fragment key={post.post_id}>
                      <InfoCard onClick={() => handlePostClick(post.post_id)}>
                        <InfoContent>
                          <InfoHeader>
                            <InfoTitle>{post.title}</InfoTitle>
                            <InfoDescription>
                              {post.content.replace(/<[^>]*>/g, '').length > 100 
                                ? post.content.replace(/<[^>]*>/g, '').substring(0, 100) + '...' 
                                : post.content.replace(/<[^>]*>/g, '')}
                            </InfoDescription>
                          </InfoHeader>
                          
                          <InfoFooter>
                            <InfoAuthorInfo>
                              <AuthorAvatar>
                                <img src="/images/default-avatar.png" alt="작성자" />
                              </AuthorAvatar>
                              <AuthorName>{post.writer_name}</AuthorName>
                              <Divider />
                              <InfoDate>{new Date(post.created_at).toLocaleDateString('ko-KR')}</InfoDate>
                            </InfoAuthorInfo>
                            
                            <InfoStats>
                              <StatItem>댓글 {post.reply_cnt}</StatItem>
                              <Divider />
                              <StatItem>조회수 {post.view_cnt}</StatItem>
                            </InfoStats>
                          </InfoFooter>
                        </InfoContent>
                      </InfoCard>
                      
                      {index < regularPosts.length - 1 && <InfoDivider />}
                    </React.Fragment>
                  ))
                )}
              </>
            )}
          </InfoList>

          {/* 더보기 버튼 */}
          {/* {hasMorePosts && (
            <LoadMoreContainer>
              <LoadMoreButton onClick={loadMorePosts} disabled={loading}>
                {loading ? '로딩 중...' : '더보기'}
              </LoadMoreButton>
            </LoadMoreContainer>
          )} */}
        </MainContainer>
      </Container>
    </CommunityLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 0 128px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 20px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: rgba(55, 56, 60, 0.28);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const InfoCard = styled.div`
  padding: 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: rgba(112, 115, 124, 0.16);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoTitle = styled.h3`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`;

const InfoDescription = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
`;

const InfoFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoAuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.16);
`;

const InfoDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const InfoStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const InfoDivider = styled.div`
  height: 1px;
  background: rgba(112, 115, 124, 0.08);
  margin: 16px 0;
`;

const LoadMoreContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const LoadMoreButton = styled.button<{ disabled: boolean }>`
  padding: 12px 24px;
  background: ${props => props.disabled ? '#f5f5f5' : '#448181'};
  color: ${props => props.disabled ? '#999' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? '#f5f5f5' : '#3a6b6b'};
  }
`;

export default InfoSharing;
